import { Control, Controller, FieldValues, Path } from 'react-hook-form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@alphabait/ui'
import { useTranslation } from 'react-i18next'
import styles from '../form/form.module.css'
import cx from 'classnames'

type SelectOption = {
  label: string
  value: string
  disabled?: boolean
}

interface FormSelectProps<T extends FieldValues = FieldValues> {
  name: Path<T>
  control: Control<T>
  options: SelectOption[]
  label?: string
  placeholder?: string
  wrapperClassName?: string
  labelClassName?: string
  triggerClassName?: string
  testId?: string
  disabled?: boolean
}

export function FormSelect<T extends FieldValues = FieldValues>({
  name,
  control,
  options,
  label,
  placeholder,
  wrapperClassName,
  labelClassName,
  triggerClassName,
  testId,
  disabled,
}: FormSelectProps<T>) {
  const { t } = useTranslation()

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        // Filter out options with empty string values (shadcn doesn't allow them)
        const validOptions = options.filter((opt) => opt.value !== '')
        // Convert empty string to undefined for shadcn Select
        const selectValue = field.value === '' ? undefined : field.value

        return (
          <div className={cx('mb-4', wrapperClassName)}>
            {label && (
              <label className={cx('text-md text-grey-10000', labelClassName)}>
                {label}
              </label>
            )}
            <Select
              onValueChange={(value) => field.onChange(value as any)}
              value={selectValue}
              disabled={disabled}
            >
              <SelectTrigger
                className={cx(triggerClassName)}
                data-testid={testId}
              >
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
              {/* Admin form: keep the list out of the end-user preview's .theme-default box. */}
              <SelectContent container={document.body}>
                {validOptions.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fieldState.error && (
              <span
                className={styles.errorMessage}
                data-testid={`${testId}-error`}
              >
                {t(`validation_messages.${fieldState.error.message}`)}
              </span>
            )}
          </div>
        )
      }}
    />
  )
}
